import React, { useEffect, useState, useRef } from 'react';
import { Drawer, Box, Typography, IconButton, Modal, Grid, alpha, Paper } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { Close as CloseIcon, TrendingUp, TrendingDown } from '@mui/icons-material';
import { FormProvider, RHFTextField } from '../../../../components/hook-form';
// import PriceWithColor from './PriceWithColor';
import RHFToggleButtonGroup from '../../../../components/hook-form/RHFToggleButtonGroup';
import axiosInstance from '../../../../utils/axios';
import modifyScriptName from '../../../../utils/modifyScriptName';

export const Wrapper = ({ children, open, onClose, isMobile }) => {
  return isMobile ? (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100vw',
          height: '100vh',
          bgcolor: 'background.default'
        }
      }}
    >
      {children}
    </Drawer>
  ) : (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          border: 'none',
          p: 4,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {children}
      </Box>
    </Modal>
  );
};

// const QuoteValueItem = ({ label, value }) => (
//   <Box display="flex" justifyContent="space-between">
//     <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
//       {label}
//     </Typography>
//     <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
//       :
//     </Typography>
//     <Typography variant="body1" textAlign="right">
//       {value}
//     </Typography>
//   </Box>
// );
const QuoteValueItem = ({ label, value }) => {
  const theme = useTheme();

  // Format numerical values to always have 2 decimal places for consistency
  const formattedValue = typeof value === 'number'
    ? value.toFixed(2)
    : (value || '0.00');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.5,
        px: 1,
        borderRadius: 1,
        '&:hover': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
          fontSize: '0.8125rem',
          flexBasis: '40%',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          fontFamily: 'Roboto Mono, monospace',
          color: 'text.primary',
          flexBasis: '60%',
          textAlign: 'right',
          letterSpacing: '0.5px',
          minWidth: '70px',
        }}
      >
        {formattedValue}
      </Typography>
    </Box>
  );
};

function BuyScriptDrawer({ open, onClose, script, isMobile }) {
  // const { quotesData } = useSelector((state) => state?.trade);
  const [scriptQuote, setScriptQuote] = useState(null);
  const updatedData = useSelector((state) => state?.trade?.quotesData[script?.instrument_token]);
  const { enqueueSnackbar } = useSnackbar();

  // For increment/decrement responsiveness
  const intervalRef = useRef(null);
  const startDelay = 400; // ms before starting continuous increment/decrement
  const intervalSpeed = 100; // ms between each increment/decrement
  useEffect(() => {
    const fetchScriptPrice = async () => {
      try {
        if (script) {
          const response = await axiosInstance.get(`/script/quote?scriptId=${script._id}`);
          const quote = Object.values(response.data.data)[0];
          setScriptQuote(quote);
        }
      } catch (err) {
        console.log('ERR', err);
      }
    };
    fetchScriptPrice();
  }, [script]);

  const defaultValues = {
    quantity: 1,
    limit: 0,
    orderType: 'MARKET',
  };

  const schema = yup.object().shape({
    quantity: yup
      .number()
      .integer('Quantity must be an integer')
      .typeError('Quantity must be a number')
      .required('Quantity is required')
      .positive('Quantity must be a positive number'),
    limit: yup.number().when('orderType', {
      is: 'LIMIT',
      then: yup
        .number()
        .typeError('Limit must be a number')
        .required('Limit is required')
        .positive('Limit must be a positive number'),
    }),
    orderType: yup.string().required('Order type is required').oneOf(['MARKET', 'LIMIT'], 'Invalid order type'),
  });

  const methods = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting },
  } = methods;

  const orderType = watch('orderType');

  useEffect(() => {
    if (orderType === 'LIMIT') {
      setValue('limit', updatedData.depth?.buy[0]?.price || 0);
    }
  }, [orderType, scriptQuote, setValue]);

  // console.log('SCRIPT', script);
  const onSubmit = async (data, event) => {
    try {
      if (data.orderType === 'MARKET') {
        delete data.limit;
      }
      data.scriptId = script._id;
      // data.side = event.nativeEvent.submitter.innerText;
      data.side = event.nativeEvent.submitter.getAttribute('data-side');
      const response = await axiosInstance.post('/trade/add', data);
      enqueueSnackbar(response.data.message);
      if (response.data.tradesCancelled) {
        enqueueSnackbar(response.data.cancelMessage, { persist: true });
      }
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };


  const handleIncrement = () => {
    const currentValue = parseInt(methods.getValues('quantity') || 1, 10);
    const newValue = currentValue + 1;
    setValue('quantity', newValue, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
  };

  const handleDecrement = () => {
    const currentValue = parseInt(methods.getValues('quantity') || 1, 10);
    const newValue = Math.max(1, currentValue - 1);
    setValue('quantity', newValue, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
  };

  if (!script) return null;
  return (
    <Wrapper
      isMobile={isMobile}
      open={open}
      onClose={onClose}
    >
      <Box
        width={{ xs: '100%', sm: '380px' }}
        height={isMobile ? '100vh' : 'auto'}
        marginX={'auto'}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            pb: 1.5,
            pt: isMobile ? 3 : 1.5,
            px: 2,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
              },
            }}
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>          <Typography
            variant="h5"
            textAlign={'center'}
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: 0.5,
              mt: 0.5,
              textTransform: 'uppercase'
            }}
          >
            {modifyScriptName(script.tradingsymbol, script.expiry)}
          </Typography>

          {/* Added spacing after form */}
          <Box sx={{ mt: 3, mb: 2 }} />
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Box sx={{ mb: 2 }}>
                <RHFToggleButtonGroup
                  name="orderType"
                  label="Order Type"
                  color="primary"
                  defaultValue={defaultValues.orderType} sx={{
                    width: '100%',
                    '& .MuiToggleButtonGroup-root': {
                      width: '100%',
                      height: 48,
                      bgcolor: 'background.neutral',
                      borderRadius: 0,
                      p: 0.5
                    },
                    '& .MuiToggleButton-root': {
                      flex: 1,
                      borderRadius: 0,
                      border: 'none',
                      fontSize: 16,
                      fontWeight: 600,
                      textTransform: 'none',
                      color: 'text.primary',
                      '&.Mui-selected': {
                        bgcolor: 'background.paper',
                        color: 'success.main',
                        '&:hover': {
                          bgcolor: 'background.paper',
                        },
                      },
                      '&:not(.Mui-selected)': {
                        bgcolor: 'transparent',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      },
                    },
                  }}
                  options={[
                    { label: 'MARKET ORDER', value: 'MARKET' },
                    { label: 'LIMIT ORDER', value: 'LIMIT' },
                  ]}
                />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <RHFTextField
                    name="quantity"
                    label="QUANTITY"
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <IconButton
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent focus issues
                            handleDecrement();
                          }}
                          sx={{
                            border: '1px solid',
                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                            borderRadius: 1,
                            mr: 1,
                            width: 36,
                            height: 36,
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                            color: 'primary.main',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                              borderColor: 'primary.main',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
                            },
                            '&:active': {
                              transform: 'translateY(0)',
                            },
                            touchAction: 'manipulation',
                          }}
                        >
                          <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: 0 }}>−</Box>
                        </IconButton>
                      ),
                      endAdornment: (
                        <IconButton
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent focus issues
                            handleIncrement();
                          }}
                          sx={{
                            border: '1px solid',
                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                            borderRadius: 1,
                            ml: 1,
                            width: 36,
                            height: 36,
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                            color: 'primary.main',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                              borderColor: 'primary.main',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
                            },
                            '&:active': {
                              transform: 'translateY(0)',
                            },
                            touchAction: 'manipulation',
                          }}
                        >
                          <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: 0 }}>+</Box>
                        </IconButton>
                      ),
                    }} sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 52,
                        borderRadius: 1.5,
                        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.8),
                        boxShadow: '0 1px 4px 0 rgba(0,0,0,0.05)',
                        transition: 'all 0.2s ease-in-out',
                        '& input': {
                          textAlign: 'center',
                          fontSize: 20,
                          fontWeight: 600,
                          fontFamily: 'Roboto Mono, monospace',
                          letterSpacing: 1,
                          color: 'text.primary',
                          paddingX: 1,
                          WebkitAppearance: 'none',
                          MozAppearance: 'textfield',
                          '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                        },
                        '& fieldset': {
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                          borderWidth: '1px',
                        },
                        '&:hover': {
                          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '1px',
                        },
                        '&.Mui-focused': {
                          boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '1px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: 'text.secondary',
                        letterSpacing: '0.5px',
                      }, '& .MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: 'background.paper',
                        paddingX: 0.5,
                      },
                    }}
                  />

                  {methods.getValues().orderType === 'LIMIT' && (
                    <RHFTextField
                      name="limit"
                      label="LIMIT PRICE"
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        mt: 2,
                        '& .MuiOutlinedInput-root': {
                          height: 52,
                          borderRadius: 1.5,
                          backgroundColor: (theme) => alpha(theme.palette.background.neutral, 0.9),
                          boxShadow: '0 1px 4px 0 rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease-in-out',
                          '& input': {
                            textAlign: 'center',
                            fontSize: 20,
                            fontWeight: 600,
                            fontFamily: 'Roboto Mono, monospace',
                            letterSpacing: 1,
                            color: 'text.primary',
                            paddingX: 1,
                            WebkitAppearance: 'none',
                            MozAppearance: 'textfield',
                            '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                          },
                          '& fieldset': {
                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                            borderWidth: '1px',
                          },
                          '&:hover': {
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: '1px',
                          },
                          '&.Mui-focused': {
                            boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: '1px',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: 'text.secondary',
                          letterSpacing: '0.5px',
                        },
                        '& .MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)', backgroundColor: 'background.neutral',
                          paddingX: 0.5,
                        },
                      }}
                    />
                  )}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, my: 2 }}>
                  <LoadingButton
                    type="submit"
                    data-side="BUY"
                    sx={{
                      height: 56,
                      bgcolor: 'success.main',
                      color: 'common.white',
                      borderRadius: 0,
                      '&:hover': {
                        bgcolor: 'success.dark',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.6,
                      },
                      textTransform: 'uppercase',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      letterSpacing: '0.5px',
                    }}
                    loading={isSubmitting}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', width: '100%' }}>
                      <TrendingUp fontSize="small" />
                      <Typography variant="button" sx={{ fontWeight: 600 }}>
                        {methods.getValues().orderType === 'MARKET' ? 'BUY' : 'BUY ORDER'}
                      </Typography>
                    </Box>
                    {methods.getValues().orderType === 'MARKET' && (
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: 'Roboto Mono, monospace',
                          fontWeight: 600,
                          letterSpacing: '1px',
                        }}
                      >
                        {+updatedData?.depth?.sell[0]?.price.toFixed(2) ||
                          (+updatedData?.ohlc?.high ? +updatedData?.ohlc?.close.toFixed(2) : 0)}
                      </Typography>
                    )}
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    data-side="SELL"
                    sx={{
                      height: 56,
                      bgcolor: 'error.main',
                      color: 'common.white',
                      borderRadius: 0,
                      '&:hover': {
                        bgcolor: 'error.dark',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.6,
                      },
                      textTransform: 'uppercase',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      letterSpacing: '0.5px',
                    }}
                    loading={isSubmitting}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', width: '100%' }}>
                      <TrendingDown fontSize="small" />
                      <Typography variant="button" sx={{ fontWeight: 600 }}>
                        {methods.getValues().orderType === 'MARKET' ? 'SELL' : 'SELL ORDER'}
                      </Typography>
                    </Box>
                    {methods.getValues().orderType === 'MARKET' && (
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: 'Roboto Mono, monospace',
                          fontWeight: 600,
                          letterSpacing: '1px',
                        }}
                      >
                        {+updatedData?.depth?.buy[0]?.price.toFixed(2) ||
                          (+updatedData?.ohlc?.high ? +updatedData?.ohlc?.close.toFixed(2) : 0)}
                      </Typography>
                    )}
                  </LoadingButton>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'background.neutral',
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.grey[500], 0.12),
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 2,
                      '& > div': {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }
                    }}
                  ><Box>
                      {methods.getValues().orderType === 'LIMIT' && (
                        <QuoteValueItem
                          label={'Bid'}
                          value={
                            +updatedData?.depth?.buy[0]?.price ||
                            (+updatedData?.ohlc?.high ? +updatedData?.ohlc?.close : 0)
                          }
                        />
                      )}
                      <QuoteValueItem label={'Open'} value={+updatedData?.ohlc?.open || 0} />
                      <QuoteValueItem label={'High'} value={+updatedData?.ohlc?.high || 0} />
                      <QuoteValueItem label={'UC'} value={+scriptQuote?.upper_circuit_limit || 0} />
                    </Box>
                    <Box>
                      {methods.getValues().orderType === 'LIMIT' && (
                        <QuoteValueItem
                          label={'Ask'}
                          value={
                            +updatedData?.depth?.sell[0]?.price ||
                            (+updatedData?.ohlc?.high ? +updatedData?.ohlc?.close : 0)
                          }
                        />
                      )}
                      <QuoteValueItem label={'Close'} value={+updatedData?.ohlc?.close || 0} />
                      <QuoteValueItem label={'Low'} value={+updatedData?.ohlc?.low || 0} />
                      <QuoteValueItem label={'LC'} value={+scriptQuote?.lower_circuit_limit || 0} />
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </FormProvider>
        </Box>
      </Box>
    </Wrapper>
  );
}

export default BuyScriptDrawer;
